import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ExternalLink, BarChart3, FileText, ArrowUp, ArrowDown } from 'lucide-react';
import { format } from 'date-fns';
import {
  AdminPageHeader,
  AdminPanel,
  AdminEmptyState,
  AdminPageSkeleton,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from './AdminUI';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  read_time: string;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  published_at: string | null;
  is_featured: boolean;
  updated_at: string;
  created_at: string;
}

function statusTone(s: BlogPost['status']): 'neutral' | 'warning' | 'success' | 'accent' {
  switch (s) {
    case 'published': return 'success';
    case 'scheduled': return 'warning';
    case 'archived': return 'accent';
    default: return 'neutral';
  }
}

type SortField = 'created_at' | 'updated_at' | 'published_at' | 'title' | 'status';
type SortDir = 'asc' | 'desc';

const SORT_LABELS: Record<SortField, string> = {
  created_at: 'Created',
  updated_at: 'Updated',
  published_at: 'Publish date',
  title: 'Title',
  status: 'Status',
};

export function AdminBlogTab() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [statusFilter, setStatusFilter] = useState<'all' | BlogPost['status']>('all');

  const fetchPosts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select('id,slug,title,category,read_time,status,published_at,is_featured,updated_at,created_at')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    else setPosts((data || []) as BlogPost[]);
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, []);

  const filteredAndSorted = useMemo(() => {
    let list = statusFilter === 'all' ? [...posts] : posts.filter((p) => p.status === statusFilter);
    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'title') {
        return a.title.localeCompare(b.title) * dir;
      }
      if (sortField === 'status') {
        const order = ['draft', 'scheduled', 'published', 'archived'];
        const ai = order.indexOf(a.status);
        const bi = order.indexOf(b.status);
        return (ai - bi) * dir;
      }
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (av === bv) return 1;
      return (av < bv ? -1 : 1) * dir;
    });
    return list;
  }, [posts, sortField, sortDir, statusFilter]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this post permanently?')) return;
    const { error } = await supabase.from('blog_posts').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); fetchPosts(); }
  };

  const quickStatus = async (post: BlogPost, status: BlogPost['status']) => {
    const patch: Partial<BlogPost> = { status };
    if (status === 'published' && !post.published_at) {
      patch.published_at = new Date().toISOString();
    }
    const { error } = await supabase.from('blog_posts').update(patch).eq('id', post.id);
    if (error) toast.error(error.message);
    else { toast.success(`Marked as ${status}`); fetchPosts(); }
  };

  if (loading) return <AdminPageSkeleton stats={0} rows={6} columns={5} />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Editorial"
        title="Blog CMS"
        description="Public visitors only see posts marked Published with a publish date in the past."
        meta={`${posts.length} ${posts.length === 1 ? 'post' : 'posts'}`}
        actions={
          <>
            <Button asChild variant="outline" size="sm" className="gap-1.5 h-9">
              <Link to="/admin/blog/analytics">
                <BarChart3 className="w-3.5 h-3.5" /> Analytics
              </Link>
            </Button>
            <Button asChild size="sm" className="gap-1.5 h-9">
              <Link to="/admin/blog/new">
                <Plus className="w-3.5 h-3.5" /> New post
              </Link>
            </Button>
          </>
        }
      />

      {posts.length === 0 ? (
        <AdminEmptyState
          icon={FileText}
          title="No posts yet"
          description="Click New post to publish your first one."
          action={
            <Button asChild size="sm">
              <Link to="/admin/blog/new"><Plus className="w-3.5 h-3.5 mr-1.5" /> New post</Link>
            </Button>
          }
        />
      ) : (
        <>
          {/* Filter & Sort toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Status</span>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
                <SelectTrigger className="h-8 text-xs w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Sort by</span>
              <Select value={sortField} onValueChange={(v) => setSortField(v as SortField)}>
                <SelectTrigger className="h-8 text-xs w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SORT_LABELS) as SortField[]).map((f) => (
                    <SelectItem key={f} value={f}>{SORT_LABELS[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortDir === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              </Button>
            </div>
            {statusFilter !== 'all' && (
              <span className="text-[11px] text-muted-foreground ml-auto">
                {filteredAndSorted.length} of {posts.length} posts
              </span>
            )}
          </div>

          {filteredAndSorted.length === 0 ? (
            <AdminEmptyState
              icon={FileText}
              title="No matching posts"
              description="Try changing the status filter."
            />
          ) : (
            <AdminPanel>
              <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead style={{ borderBottom: '1px solid rgba(58,53,48,0.06)' }}>
                  <tr>
                    <th className={adminTableHeadCellClass}>Title</th>
                    <th className={adminTableHeadCellClass}>Status</th>
                    <th className={adminTableHeadCellClass}>Publish date</th>
                    <th className={adminTableHeadCellClass}>Updated</th>
                    <th className="w-32" />
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.map((p, i) => {
                    const isLive = p.status === 'published' && (!p.published_at || new Date(p.published_at) <= new Date());
                    return (
                      <tr key={p.id} className={adminTableRowClass} style={{ borderTop: i === 0 ? 'none' : '1px solid rgba(58,53,48,0.06)' }}>
                        <td className={adminTableCellClass}>
                          <Link to={`/admin/blog/edit/${p.id}`} className="text-foreground hover:text-[#c2541a] transition-colors" style={{ fontWeight: 500 }}>
                            {p.title}
                          </Link>
                          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5">
                            <span>/{p.slug}</span>
                            <span>&middot;</span>
                            <span>{p.category}</span>
                            <span>&middot;</span>
                            <span>{p.read_time}</span>
                            {p.is_featured && <span style={{ color: '#c2541a' }}>&middot; &#9733; Featured</span>}
                          </div>
                        </td>
                        <td className={adminTableCellClass}>
                          <AdminPill tone={statusTone(p.status)}>{p.status}</AdminPill>
                          {p.status === 'published' && !isLive && (
                            <div className="text-[10px] text-amber-600 mt-1">Hidden until publish date</div>
                          )}
                        </td>
                        <td className={adminTableCellClass + ' text-muted-foreground tabular-nums'}>
                          {p.published_at ? format(new Date(p.published_at), 'd MMM yyyy HH:mm') : '—'}
                        </td>
                        <td className={adminTableCellClass + ' text-muted-foreground tabular-nums'}>
                          {format(new Date(p.updated_at), 'd MMM yyyy')}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            {isLive && (
                              <Button asChild size="sm" variant="ghost" className="h-7 w-7 p-0">
                                <a href={`/resources/${p.slug}`} target="_blank" rel="noreferrer">
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              </Button>
                            )}
                            {p.status !== 'published' && (
                              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => quickStatus(p, 'published')}>
                                Publish
                              </Button>
                            )}
                            {p.status === 'published' && (
                              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => quickStatus(p, 'draft')}>
                                Unpublish
                              </Button>
                            )}
                            <Button asChild size="sm" variant="ghost" className="h-7 w-7 p-0">
                              <Link to={`/admin/blog/edit/${p.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Link>
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete(p.id)}>
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </AdminPanel>
          )}
        </>
      )}
    </div>
  );
}
